import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getRun } from "../../api/client";
import { type RunDetail, Verdict } from "../../api/models";
import { BasisBadge, StatusBadge, VerdictBadge } from "../../components/Badge";
import { CatalogThumb } from "../../components/CatalogThumb";
import { EngineDetails } from "../../components/EngineDetails";
import { Progress } from "../../components/Progress";
import { usePoll } from "../../hooks/usePoll";
import { accuracy, pct } from "../../lib/accuracy";
import { errorMessage, formatDate } from "../../lib/format";
import { formatVerdict, VERDICT_ORDER } from "../../lib/verdict";
import { sortRunCards } from "./sort";

export function RunPage() {
  const id = Number(useParams().id);
  const [run, setRun] = useState<RunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Verdict | "">("");

  const load = useCallback(() => {
    getRun(id)
      .then(setRun)
      .catch((e) => setError(errorMessage(e)));
  }, [id]);
  usePoll(load, 3000, run === null || run.status === "RUNNING" || run.status === "QUEUED");

  const rows = useMemo(() => {
    if (!run) return [];
    const sorted = sortRunCards(run.cards);
    return filter ? sorted.filter((c) => c.scored.verdict === filter) : sorted;
  }, [run, filter]);

  if (error) return <p className="error">{error}</p>;
  if (!run) return <p className="muted">Loading…</p>;
  const s = run.summary;
  const acc = accuracy(s);

  return (
    <>
      <div className="toolbar">
        <Link to="/runs">‹ Runs</Link>
        <h1 style={{ margin: 0 }}>Run #{run.id}</h1>
        <StatusBadge status={run.status} />
        <Progress done={run.done} total={run.total} />
        <span className="muted">
          {run.createdBy} · {formatDate(run.createdAt)} ·{" "}
          {run.params.source === "report" ? (
            `report${run.params.label ? ` · ${run.params.label}` : ""}${run.private ? " · private" : ""}`
          ) : run.params.fetchId != null ? (
            <Link to="/fetches">fetch #{run.params.fetchId}</Link>
          ) : (
            run.params.statuses.join(", ")
          )}
          {run.params.limit ? ` · limit ${run.params.limit}` : ""}
        </span>
        <EngineDetails engine={run.engine} />
        {run.error && <span className="error">{run.error}</span>}
      </div>

      {s && (
        <div className="summary-grid">
          <div className="stat">
            <div className="big">
              {s.correct}/{s.scored}
            </div>
            <div className="sub">correct of scored · {acc.correct}</div>
          </div>
          <div className="stat">
            <div className="big">
              {s.verifiedCorrect}/{s.verified}
            </div>
            <div className="sub">verified correct · {acc.verified}</div>
          </div>
          <div className="stat">
            <div className="big">{s.ebayListings}</div>
            <div className="sub">cards with listings · {pct(s.ebayListings, s.total)}</div>
          </div>
          <div className="stat">
            <div className="big">{s.cappedQueries}</div>
            <div className="sub">capped queries</div>
          </div>
          <div className="stat">
            {VERDICT_ORDER.map((v) => (
              <div key={v}>
                <VerdictBadge verdict={v} /> <span className="mono">{s.verdicts[v]}</span>
              </div>
            ))}
          </div>
          <div className="stat">
            <h3>By type</h3>
            <table className="compact">
              <tbody>
                {Object.entries(s.byType).map(([t, v]) => (
                  <tr key={t}>
                    <td>{t}</td>
                    <td className="num">
                      {v.correct}/{v.scored}
                    </td>
                    <td className="num">{pct(v.correct, v.scored)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="stat">
            <h3>Fields</h3>
            <table className="compact">
              <tbody>
                {Object.entries(s.fields).map(([f, v]) => (
                  <tr key={f}>
                    <td>{f}</td>
                    <td className="num">
                      {v.ok}/{v.total}
                    </td>
                    <td className="num">{pct(v.ok, v.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="toolbar">
        <label className="field">
          Verdict
          <select value={filter} onChange={(e) => setFilter(e.target.value as Verdict | "")}>
            <option value="">All</option>
            {Verdict.options.map((v) => (
              <option key={v} value={v}>
                {formatVerdict(v).label}
              </option>
            ))}
          </select>
        </label>
        <span className="muted">
          {rows.length} of {run.cards.length}
        </span>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Photo</th>
              <th>Scan</th>
              <th>Basis</th>
              <th>Truth</th>
              <th>Picked</th>
              <th>Verdict</th>
              <th className="num">Diffs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const truthId = c.truth.productId;
              const differs = c.pickedId !== truthId;
              return (
                <tr key={c.id}>
                  <td>
                    <img className="photo-thumb" src={c.card.photoUrl} alt="" loading="lazy" />
                  </td>
                  <td className="mono">
                    <Link to={`/cards/${c.cardId}`}>{c.card.scanId}</Link>
                  </td>
                  <td>
                    <BasisBadge basis={c.scored.basis} />
                  </td>
                  <td>
                    {truthId ? (
                      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <CatalogThumb url={run.images[truthId]} small />
                        <span className="mono">{truthId}</span>
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {c.pickedId && differs ? (
                      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <CatalogThumb url={run.images[c.pickedId]} small />
                        <span>
                          <span className="mono">{c.pickedId}</span> {c.pickedName}
                        </span>
                      </span>
                    ) : c.pickedId ? (
                      <span className="muted">same</span>
                    ) : (
                      <span className="muted">{c.error ? `${c.error.stage}: ${c.error.message}` : "no pick"}</span>
                    )}
                  </td>
                  <td>
                    <VerdictBadge verdict={c.scored.verdict} />
                  </td>
                  <td className="num">{c.scored.diffs.length}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  No cards.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
